import { supabase, User, Goal, TalentTree, TalentNode, HealthWorthAssessment } from '../lib/supabase';

// Auth Services
export const authService = {
  async signUp(email: string, password: string, name: string) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          name,
          full_name: name,
        },
        emailRedirectTo: `${window.location.origin}/email-confirmed`,
      },
    });

    if (error) throw error;

    // Don't create user profile or try to fetch it - they need to confirm email first
    // The profile will be created when they confirm their email and sign in
    
    return data;
  },

  async signIn(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;
    return data;
  },

  async signOut() {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },

  async getCurrentUser() {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) return null;

    // First, try to get the existing profile
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', user.id)
      .single();

    if (error && error.code === 'PGRST116') {
      // Profile doesn't exist, create it using upsert to handle duplicates
      console.log('Profile not found, creating new profile...');
      
      try {
        const newProfile = {
          id: user.id,
          email: user.email || '',
          name: user.user_metadata?.name || user.email?.split('@')[0] || 'User',
          subscription_tier: 'free',
          subscription_plan: 'free',
          subscription_status: 'inactive',
          weekly_time_allocation: 40
        };

        // Use upsert to handle potential duplicate emails gracefully
        const { data: createdProfile, error: createError } = await supabase
          .from('users')
          .upsert([newProfile], { 
            onConflict: 'id,email',
            ignoreDuplicates: true 
          })
          .select()
          .single();

        if (createError) {
          console.error('Error creating profile:', createError);
          
          // If it's a unique constraint violation on email, try to find existing user
          if (createError.message?.includes('unique') || createError.message?.includes('duplicate')) {
            console.log('Duplicate email detected, searching for existing user...');
            const { data: existingUser, error: searchError } = await supabase
              .from('users')
              .select('*')
              .eq('email', user.email)
              .single();
            
            if (!searchError && existingUser) {
              console.log('Found existing user with same email:', existingUser);
              return existingUser;
            }
          }
          
          throw new Error(`Failed to create user profile: ${createError.message}`);
        }

        console.log('Profile created successfully:', createdProfile);
        return createdProfile;
      } catch (createError) {
        console.error('Profile creation failed:', createError);
        throw new Error('Failed to create user profile. Please try refreshing the page.');
      }
    }

    if (error) {
      console.error('Error fetching profile:', error);
      throw error;
    }

    if (data) {
      console.log('Profile successfully loaded:', data);
      return data;
    }

    throw new Error('Unable to load user profile. Please refresh the page.');
  },
};

// Goals Services
export const goalsService = {
  async getGoals(userId: string): Promise<Goal[]> {
    const { data, error } = await supabase
      .from('goals')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async createGoal(goal: Omit<Goal, 'id' | 'created_at' | 'updated_at'>): Promise<Goal> {
    const { data, error } = await supabase
      .from('goals')
      .insert([goal])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateGoal(id: string, updates: Partial<Goal>): Promise<Goal> {
    const { data, error } = await supabase
      .from('goals')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deleteGoal(id: string): Promise<void> {
    const { error } = await supabase
      .from('goals')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },
};

// Talent Trees Services
export const talentTreesService = {
  async getTalentTrees(userId: string): Promise<TalentTree[]> {
    const { data, error } = await supabase
      .from('talent_trees')
      .select('*')
      .eq('user_id', userId);

    if (error) throw error;
    
    // If user has no talent trees, create default ones
    if (!data || data.length === 0) {
      await this.createDefaultTalentTrees(userId);
      // Fetch again after creating defaults
      const { data: newData, error: newError } = await supabase
        .from('talent_trees')
        .select('*')
        .eq('user_id', userId);
      
      if (newError) throw newError;
      return newData || [];
    }
    
    return data;
  },

  async createDefaultTalentTrees(userId: string) {
    const defaultTrees = [
      {
        user_id: userId,
        tree_id: 'health-fitness',
        name: 'Health & Fitness',
        description: 'Physical health and fitness development',
        nodes: [
          {
            id: 'cardio-1',
            name: 'Basic Cardio',
            description: 'Build cardiovascular endurance through regular cardio exercises',
            category: 'health',
            level: 0,
            maxLevel: 5,
            timeRequired: 3,
            prerequisites: [],
            unlocked: true,
            progress: 0,
            benefits: ['Improved heart health', 'Better stamina', 'Weight management']
          },
          {
            id: 'strength-1',
            name: 'Strength Training',
            description: 'Develop muscle strength and bone density',
            category: 'health',
            level: 0,
            maxLevel: 5,
            timeRequired: 4,
            prerequisites: [],
            unlocked: true,
            progress: 0,
            benefits: ['Increased muscle mass', 'Better metabolism', 'Injury prevention']
          },
          {
            id: 'flexibility-1',
            name: 'Flexibility & Mobility',
            description: 'Improve range of motion and prevent injuries',
            category: 'health',
            level: 0,
            maxLevel: 5,
            timeRequired: 2,
            prerequisites: [],
            unlocked: false,
            progress: 0,
            benefits: ['Better posture', 'Injury prevention', 'Enhanced performance']
          }
        ],
        total_time_allocated: 0
      },
      {
        user_id: userId,
        tree_id: 'professional-skills',
        name: 'Professional Skills',
        description: 'Career and professional development',
        nodes: [
          {
            id: 'communication-1',
            name: 'Communication Skills',
            description: 'Improve verbal and written communication',
            category: 'skills',
            level: 0,
            maxLevel: 5,
            timeRequired: 3,
            prerequisites: [],
            unlocked: true,
            progress: 0,
            benefits: ['Better presentations', 'Clear writing', 'Active listening']
          },
          {
            id: 'leadership-1',
            name: 'Leadership Fundamentals',
            description: 'Develop basic leadership and management skills',
            category: 'skills',
            level: 0,
            maxLevel: 5,
            timeRequired: 4,
            prerequisites: ['communication-1'],
            unlocked: false,
            progress: 0,
            benefits: ['Team management', 'Decision making', 'Conflict resolution']
          }
        ],
        total_time_allocated: 0
      }
    ];

    try {
      const { error } = await supabase
        .from('talent_trees')
        .insert(defaultTrees);
      
      if (error) throw error;
    } catch (error) {
      console.warn('Failed to create default talent trees:', error);
    }
  },

  async createTalentTree(tree: Omit<TalentTree, 'id' | 'created_at' | 'updated_at'>): Promise<TalentTree> {
    const { data, error } = await supabase
      .from('talent_trees')
      .insert([tree])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateTalentTree(id: string, updates: Partial<TalentTree>): Promise<TalentTree> {
    const { data, error } = await supabase
      .from('talent_trees')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async getTalentNodes(treeId: string): Promise<TalentNode[]> {
    const { data, error } = await supabase
      .from('talent_nodes')
      .select('*')
      .eq('tree_id', treeId);

    if (error) throw error;
    return data || [];
  },

  async updateTalentNode(id: string, updates: Partial<TalentNode>): Promise<TalentNode> {
    const { data, error } = await supabase
      .from('talent_nodes')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },
};

// Analytics Services
export const analyticsService = {
  async getWeeklyProgress(userId: string, weeks: number = 4) {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - (weeks * 7));

    const { data, error } = await supabase
      .from('goals')
      .select('category, time_required, progress, updated_at')
      .eq('user_id', userId)
      .gte('updated_at', startDate.toISOString());

    if (error) throw error;
    return data || [];
  },

  async getCategoryDistribution(userId: string) {
    const { data, error } = await supabase
      .from('goals')
      .select('category, time_required')
      .eq('user_id', userId)
      .eq('status', 'active');

    if (error) throw error;
    return data || [];
  },
};

// Edge Functions Services
export const edgeFunctionsService = {
  async calculateUserStats() {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const { data, error } = await supabase.functions.invoke('calculate-user-stats', {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (error) throw error;
    return data;
  },

  async completeGoalWithRewards(goalId: string) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const { data, error } = await supabase.functions.invoke('complete-goal', {
      body: { goalId },
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (error) throw error;
    return data;
  },

  async generateRecommendations() {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const { data, error } = await supabase.functions.invoke('generate-recommendations', {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (error) throw error;
    return data;
  },
};

// User Services
export const userService = {
  async updateUser(id: string, updates: Partial<User>): Promise<User> {
    const { data, error } = await supabase
      .from('users')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async getUserStats(userId: string) {
    // Use Edge Function for comprehensive stats if available, fallback to basic stats
    try {
      return await edgeFunctionsService.calculateUserStats();
    } catch (error) {
      console.warn('Edge function unavailable, using fallback stats:', error);
      
      const [goals, talentTrees] = await Promise.all([
        goalsService.getGoals(userId),
        talentTreesService.getTalentTrees(userId),
      ]);

      const completedGoals = goals.filter(goal => goal.status === 'completed').length;
      const totalTimeAllocated = goals.reduce((sum, goal) => sum + goal.time_required, 0);

      return {
        overview: {
          totalGoals: goals.length,
          completedGoals,
          totalTimeAllocated,
          talentTreesCount: talentTrees.length,
        }
      };
    }
  },
};

// Health Worth Services
export const healthWorthService = {
  async getHealthAssessments(userId: string): Promise<HealthWorthAssessment[]> {
    const { data, error } = await supabase
      .from('health_worth_assessments')
      .select('*')
      .eq('user_id', userId)
      .order('assessment_date', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async createHealthAssessment(assessment: Omit<HealthWorthAssessment, 'id' | 'created_at' | 'updated_at'>): Promise<HealthWorthAssessment> {
    const { data, error } = await supabase
      .from('health_worth_assessments')
      .insert([assessment])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateHealthAssessment(id: string, updates: Partial<HealthWorthAssessment>): Promise<HealthWorthAssessment> {
    const { data, error } = await supabase
      .from('health_worth_assessments')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deleteHealthAssessment(id: string): Promise<void> {
    const { error } = await supabase
      .from('health_worth_assessments')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  async getLatestAssessment(userId: string): Promise<HealthWorthAssessment | null> {
    const { data, error } = await supabase
      .from('health_worth_assessments')
      .select('*')
      .eq('user_id', userId)
      .order('assessment_date', { ascending: false })
      .limit(1)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data || null;
  },
}; 